import type { JSX, ReactNode } from "react";

import BioOil from "../../assets/icons/bio-oil.svg?react";
import Forestation from "../../assets/icons/forestation.svg?react";
import Biochar from "../../assets/icons/biochar.svg?react";
import Olivine from "../../assets/icons/olivine.svg?react";
import Dacs from "../../assets/icons/dacs.svg?react";
import { CDRMethod } from "../../constants";
import {
  calculateCDRItemFee,
  changeCDRBreakdown,
  selectBreakdown,
  selectClimacruxFee,
  selectTotalCost,
  selectTotalKg,
  selectTotalPct,
} from "../../store/cdrSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import InputField from "../common/InputField";
import React from "react";

interface CDRListElement {
  id: number;
  name: string;
  icon?: JSX.Element;
  method?: CDRMethod;
}

export default function RemovalTable() {
  const totalKg = useAppSelector(selectTotalKg);
  const breakdown = useAppSelector(selectBreakdown);
  const totalPct = useAppSelector(selectTotalPct);
  const climacruxFee = useAppSelector(selectClimacruxFee);
  const totalFee = useAppSelector(selectTotalCost);

  const cdrList: CDRListElement[] = [
    {
      id: 1,
      icon: <BioOil />,
      method: CDRMethod.BIO_OIL,
      name: "Underground bio-oil injection",
    },
    {
      id: 2,
      icon: <Forestation />,
      method: CDRMethod.FORESTATION,
      name: "Afforestation through planting trees",
    },
    {
      id: 3,
      icon: <Biochar />,
      method: CDRMethod.BIOCHAR,
      name: "Biochar production & distribution",
    },
    {
      id: 4,
      icon: <Olivine />,
      method: CDRMethod.OLIVINE,
      name: "Enhanced weathering through olivine",
    },
    {
      id: 5,
      icon: <Dacs />,
      method: CDRMethod.DACS,
      name: "Direct Air Capture and Storage",
    },
    { id: 6, name: "Fees" },
    { id: 7, name: "Total" },
  ];

  const dispatch = useAppDispatch();

  function handlePctChange(method: CDRMethod) {
    const updateBreakdown = (event: React.FormEvent<HTMLInputElement>) => {
      const amountPct = +event.currentTarget.value;
      dispatch(changeCDRBreakdown({ method, amountPct }));
    };
    return updateBreakdown;
  }

  function loadMethodColumn(name: string, icon?: JSX.Element) {
    switch (name) {
      case "Fees":
        return (
          <>
            <span className="text-heading">{name}</span>
          </>
        );
      case "Total":
        return (
          <>
            <span className="text-heading font-bold">{name}</span>
          </>
        );
      default:
        return (
          <>
            <div className="hidden w-[3rem] sm:flex">{icon}</div>
            <span className="text-heading sm:pl-3">{name}</span>
          </>
        );
    }
  }

  function loadPercentageColumn(name: string, method?: CDRMethod): ReactNode {
    switch (name) {
      case "Fees":
        return "";
      case "Total":
        return <span className="pr-5 font-bold">{totalPct} %</span>;
      default:
        return (
          method && (
            <InputField
              type="percentage"
              fieldValue={breakdown[method].amountPct}
              onChange={handlePctChange(method)}
            />
          )
        );
    }
  }

  function loadAmountColumn(name: string, method?: CDRMethod) {
    switch (name) {
      case "Fees":
        return "";
      case "Total":
        return (
          <span className="font-bold">
            {new Intl.NumberFormat("en-US").format(totalKg)}{" "}
            <span className="ml-1">kg</span>
          </span>
        );
      default:
        return (
          <>
            {method &&
              new Intl.NumberFormat("en-US").format(
                (totalKg * breakdown[method].amountPct) / 100,
              )}{" "}
            <span className="ml-1 hidden sm:inline">kg</span>
          </>
        );
    }
  }

  function loadCostColumn(name: string, method?: CDRMethod) {
    switch (name) {
      case "Fees":
        return (
          <>
            <span className="hidden sm:block">
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(Math.ceil(climacruxFee) / 100)}
            </span>
            <span className="block sm:hidden">
              {new Intl.NumberFormat("en-US").format(
                Math.ceil(climacruxFee) / 100,
              )}
            </span>
          </>
        );
      case "Total":
        return (
          <>
            <span className="hidden font-bold sm:block">
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(Math.ceil(totalFee) / 100)}
            </span>
            <span className="block font-bold sm:hidden">
              {new Intl.NumberFormat("en-US").format(Math.ceil(totalFee) / 100)}
            </span>
          </>
        );

      default:
        if (method) {
          return (
            <>
              <span className="hidden sm:block">
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(
                  Math.ceil(
                    calculateCDRItemFee(
                      totalKg,
                      breakdown[method].amountPct,
                      method,
                    ),
                  ) / 100,
                )}
              </span>
              <span className="block sm:hidden">
                {new Intl.NumberFormat("en-US").format(
                  Math.ceil(
                    calculateCDRItemFee(
                      totalKg,
                      breakdown[method].amountPct,
                      method,
                    ),
                  ) / 100,
                )}
              </span>
            </>
          );
        }
    }
  }

  return (
    <>
      <div className="mt-8">
        <h3 className="text-heading text-left font-header text-2xl font-bold sm:text-center">
          How would you like to remove your carbon dioxide?
        </h3>
        <h4 className="text-heading text-left font-header text-xl font-semibold sm:text-center">
          Tweak the percentage of each removal method to your liking.
        </h4>
      </div>
      <div className="m-auto mt-8 grid max-w-7xl grid-flow-row grid-cols-4 place-content-between gap-2">
        <div className="col-span-4 grid border-t-2" />
        <h4 className="text-heading text-left font-header font-bold">
          <span className="hidden md:block">Removal method</span>
          <span className="md:hidden">Method</span>
        </h4>
        <h4 className="text-heading text-right font-header font-bold">
          <span className="hidden pr-5 md:block">Percentage [%]</span>
          <span className="pr-5 md:hidden">%</span>
        </h4>
        <h4 className="text-heading text-right font-header font-bold">
          <span className="hidden md:block">Amount [Kg]</span>
          <span className="md:hidden">Kg</span>
        </h4>
        <h4 className="text-heading text-right font-header font-bold">
          <span className="hidden md:block">Cost [USD]</span>
          <span className="md:hidden">USD</span>
        </h4>
        {cdrList.map((cdrListItem) => (
          <React.Fragment key={cdrListItem.id}>
            <div className="col-span-4 grid border-t-2" />
            <div className="text-heading flex items-center">
              {loadMethodColumn(cdrListItem.name, cdrListItem.icon)}
            </div>
            <div className="flex items-center justify-end py-4 pr-1 text-right whitespace-nowrap">
              {loadPercentageColumn(cdrListItem.name, cdrListItem.method)}
            </div>
            <p className="flex items-center justify-end py-4 text-right whitespace-nowrap">
              {loadAmountColumn(cdrListItem.name, cdrListItem.method)}
            </p>
            <p className="flex items-center justify-end py-4 text-right whitespace-nowrap">
              {loadCostColumn(cdrListItem.name, cdrListItem.method)}
            </p>
          </React.Fragment>
        ))}
      </div>
    </>
  );
}
